import { produce } from 'immer';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { customEvents } from '../../../../../common/constants';
import {
  getExtraMonth,
  getExtraYear,
  getValue,
} from '../../../../../common/global/helpers';
import useMount from '../../../../../common/hooks/useMount';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { rewriteImageUrl } from '../../../helpers';
import {
  CanCancelSubscriptionTypes,
  SubscriptionItemModel,
} from '../../../models/Subscriptions';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import CancelSubscriptionDialog from '../../Common/CancelSubscriptionDialog';

export default function Subscriptions() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState<SubscriptionItemModel[]>();
  const [activeProductId, setActiveProductId] = useState<number | null>(null);
  const activeProduct =
    _.find(subscriptions, c => c.productId == activeProductId) ?? null;

  useMount(abortController => {
    (async () => {
      const result = await productsService.getSubscriptions('', {
        signal: abortController?.signal,
      });

      if (!result.hasErrors) {
        setSubscriptions(result.data.value.filter(s => !s.isExpired));
      }
    })();
  });

  return (
    <section className="page-section mb-60">
      <p className="mb-25">
        {translate(resources.myBenefitsPage.recurringBenefitsCopy)}
      </p>

      <Loader loading={!subscriptions} loader={<LoadingSpinner />}>
        <div className="card-list card-list-5 card-list-md-5 card-list-sm-4 card-list-xs-3 card-list-xxs-2">
          {_.map(subscriptions, product => {
            const imgSrc = rewriteImageUrl(product.image);

            return (
              <div
                key={product.orderId}
                className="card product-card link-action"
              >
                <Link
                  to={`/my-account/orders/${product.orderId}`}
                  className="card-img"
                >
                  <img src={imgSrc} alt={product.name} />
                </Link>
                <div
                  onClick={() =>
                    navigate(`/my-account/orders/${product.orderId}`)
                  }
                  className="card-details"
                >
                  <a className="card-title">{product.name}</a>
                  <span className="card-info">
                    {translate(resources.myBenefitsPage.expires)}{' '}
                    {translate(
                      getValue(
                        resources.months,
                        product.displayOneMonthExtra
                          ? getExtraMonth(product.endMonth).toString()
                          : product.endMonth.toString(),
                      ),
                    )}{' '}
                    {product.displayOneMonthExtra
                      ? getExtraYear(product.endMonth, product.endYear)
                      : product.endYear}
                  </span>
                </div>
                {product.canCancelSubscription !==
                  CanCancelSubscriptionTypes.Hide && (
                  <div className="card-actions">
                    <Button
                      disabled={
                        product.canCancelSubscription ===
                        CanCancelSubscriptionTypes.Disable
                      }
                      onClick={() => setActiveProductId(product.productId)}
                      className="btn-link"
                      icon={<Icons.ArrowButton />}
                    >
                      <span className="btn-text">
                        {translate(resources.recurring.cancel)}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Loader>
      {!_.isNull(activeProduct) && (
        <CancelSubscriptionDialog
          product={activeProduct}
          onSuccess={() => {
            setSubscriptions(
              produce(draft => {
                return draft?.filter(
                  e => e.orderProductId !== activeProduct.orderProductId,
                );
              }),
            );
            document.dispatchEvent(
              new CustomEvent(customEvents.refreshCustomerBalances),
            );
          }}
          onClose={() => setActiveProductId(null)}
        />
      )}
    </section>
  );
}
